import React, { useCallback, useEffect, useState } from 'react';
import Spinner from 'src/components/ui/Features/Spinner/Spinner';
import { PageHeader } from 'src/components/ui/GlobalComponents/PageHeader/PageHeader';
import useFetch from 'src/hook/api/useFetch';
import useUser from 'src/hook/data/useUser';
import usePopUp from 'src/hook/modal/usePopUp';
import { HttpMethod } from 'src/model/enum/enums';
import { Session } from 'src/model/Session';
import { GetAllSessionsUrl, GetRevokeSessionUrl, IconLocation } from 'src/utils/URLUtils';
import styles from './ActiveDevices.module.scss';

const ActiveDevices: React.FC = () => {
    const { userInfo } = useUser();
    const { send, isLoading } = useFetch<any>();
    const [sessions, setSessions] = useState<Session[]>([]);
    const { show } = usePopUp();


    const fetchSessions = useCallback(async () => {
        if (!userInfo?.Email || !userInfo?.LastTicket) return;
        const { response } = await
            send(GetAllSessionsUrl(userInfo.Email, userInfo.LastTicket),
                HttpMethod.POST);
        if (response && Array.isArray(response)) {
            setSessions(response);
        }
    }, [send, userInfo?.Email, userInfo?.LastTicket]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    // Terminate session and update local state
    const terminateSession = async (token: string) => {
        if (!userInfo?.Email) return;
        setSessions(prev => prev.filter(s => s.TcaToken !== token));
        send(GetRevokeSessionUrl(userInfo.Email, token), HttpMethod.POST);
    };

    const handleTerminate = async (token: string) => {
        show({
            title: 'Terminate Session',
            message: '⚠️ You will be logged out of this device. Do you want to proceed?',
            primaryButtonText: 'Confirm',
            primaryButtonClick: () => terminateSession(token),
            showSecondaryButton: true,
            secondaryButtonText: 'Cancel',
        });
    };

    // Move current device to the top
    const orderedSessions = React.useMemo(() => {
        if (!userInfo?.Token) return sessions;
        const currentIdx = sessions.findIndex(s => s.TcaToken === userInfo.Token);
        if (currentIdx === -1) return sessions;
        const current = sessions[currentIdx];
        return [current, ...sessions.slice(0, currentIdx), ...sessions.slice(currentIdx + 1)];
    }, [sessions, userInfo?.Token]);

    return (
        <div className={styles.activeDevices}>
            <PageHeader title="Settings" />
            <div className={styles.activeDevicesContent}>
                <h2>Active Devices</h2>
                <div className={styles.deviceList}>
                    {isLoading && <Spinner />}
                    {orderedSessions.map((session) => (
                        <React.Fragment key={session.TcaToken}>
                            <div className={styles.deviceRow}>
                                <div className={styles.deviceInfo}>
                                    <img src={IconLocation('tablet')} alt="device" className={styles.deviceIcon} />
                                    <span className={styles.deviceName}>{session.Source}</span>
                                </div>
                                {session.TcaToken === userInfo?.Token ? (
                                    <span className={styles.currentDevice}>Current Device</span>
                                ) : (
                                    <button
                                        className={styles.terminateButton}
                                        onClick={() => handleTerminate(session.TcaToken)}
                                    >
                                        Terminate
                                    </button>
                                )}
                            </div>
                            {session.TcaToken === userInfo?.Token && <hr />}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ActiveDevices;
